"use client";

import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useWalletPortfolioContext } from "../../providers/wallet-portfolio-provider";
import { useRewardFactoryInfo } from "../../queries/use-reward-factory-info";
import { CHAIN_IDS } from "../../constants";
import type { GasEstimate, SendTransactionArgs } from "../../types";
import { encodeWithdrawErc20, toDisplayWalletError } from "../../utils";
import {
  normalizeFailureReason,
  trackDappUiFunnel,
  type TrackDappUiFunnelOptions,
} from "../../../../lib/sentry/index";
import type {
  WithdrawDialogPosition,
  WithdrawDialogStatus,
} from "./WithdrawDialog";

const WP_QUERY_PREFIX = "wp:";

function toHexAddress(address: string): `0x${string}` | null {
  return /^0x[a-fA-F0-9]{40}$/.test(address)
    ? (address as `0x${string}`)
    : null;
}

/**
 * 내장 Withdraw 다이얼로그의 트랜잭션 상태 머신.
 * Points / Rewards 등 어느 섹션에서든 `WithdrawDialog`와 짝지어 재사용한다.
 * 다이얼로그는 토큰 선택·금액 입력만 담당하고, 제출·해시·에러·복사 상태와
 * 라우터 주소 결정(`/v1/factory/info`)은 이 훅이 소유한다.
 */
export function useWithdrawFlow(section: "rewards" | "points") {
  const { env, walletAddress, sendTransaction, estimateGas } =
    useWalletPortfolioContext();
  const hasAddress = !!walletAddress;
  const canWithdrawInline = !!sendTransaction;
  const queryClient = useQueryClient();
  const { routerAddress } = useRewardFactoryInfo(
    env,
    hasAddress && canWithdrawInline,
  );
  const rewardRouterAddress = toHexAddress(routerAddress);

  const [isOpen, setIsOpen] = React.useState(false);
  const [initialPoolAddress, setInitialPoolAddress] = React.useState<
    string | undefined
  >(undefined);
  const [status, setStatus] = React.useState<WithdrawDialogStatus>("confirm");
  const [txHash, setTxHash] = React.useState<`0x${string}` | undefined>();
  const [error, setError] = React.useState("");
  const [copied, setCopied] = React.useState(false);
  const copyTimerRef = React.useRef<ReturnType<typeof setTimeout>>(undefined);

  React.useEffect(() => {
    return () => clearTimeout(copyTimerRef.current);
  }, []);

  const funnelFiredRef = React.useRef(new Set<string>());
  const trackFunnelOnce = React.useCallback(
    (funnelStep: string, funnelOptions?: TrackDappUiFunnelOptions) => {
      if (funnelFiredRef.current.has(funnelStep)) return;
      funnelFiredRef.current.add(funnelStep);
      trackDappUiFunnel("withdraw", funnelStep, {
        ...funnelOptions,
        tags: { section, ...funnelOptions?.tags },
      });
    },
    [section],
  );

  const buildWithdrawTransaction = React.useCallback(
    (poolId: number, amountRaw: bigint): SendTransactionArgs | null => {
      if (!rewardRouterAddress) return null;
      return {
        to: rewardRouterAddress,
        value: 0n,
        data: encodeWithdrawErc20(poolId, amountRaw),
        chainId: CHAIN_IDS[env].cross,
      };
    },
    [env, rewardRouterAddress],
  );

  const invalidatePortfolioQueries = React.useCallback(() => {
    queryClient.invalidateQueries({
      predicate: (query) =>
        typeof query.queryKey[0] === "string" &&
        query.queryKey[0].startsWith(WP_QUERY_PREFIX),
    });
  }, [queryClient]);

  const openWithdraw = React.useCallback(
    (poolAddress?: string) => {
      setInitialPoolAddress(poolAddress);
      setIsOpen(true);
      setStatus("confirm");
      setTxHash(undefined);
      setError("");
      setCopied(false);
      funnelFiredRef.current = new Set();
      trackFunnelOnce("opened");
    },
    [trackFunnelOnce],
  );

  const closeWithdraw = React.useCallback(() => {
    if (status === "submitting") return;
    setIsOpen(false);
    setInitialPoolAddress(undefined);
    setStatus("confirm");
    setTxHash(undefined);
    setError("");
    setCopied(false);
    if (status === "success") {
      invalidatePortfolioQueries();
    }
  }, [status, invalidatePortfolioQueries]);

  const estimateWithdraw = React.useCallback(
    async (
      position: WithdrawDialogPosition,
      amountRaw: bigint,
    ): Promise<GasEstimate | null> => {
      if (!estimateGas) return null;
      const tx = buildWithdrawTransaction(position.poolId, amountRaw);
      const from = toHexAddress(walletAddress);
      if (!tx || !from) return null;
      return estimateGas({ ...tx, from });
    },
    [buildWithdrawTransaction, estimateGas, walletAddress],
  );

  const copyHash = React.useCallback(async () => {
    if (!txHash || typeof navigator === "undefined" || !navigator.clipboard) {
      return;
    }

    try {
      await navigator.clipboard.writeText(txHash);
      setCopied(true);
      clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => setCopied(false), 1_400);
    } catch {
      setCopied(false);
    }
  }, [txHash]);

  const confirmWithdraw = React.useCallback(
    async (
      position: WithdrawDialogPosition,
      amountRaw: bigint,
      estimate: GasEstimate | null,
    ) => {
      if (!sendTransaction) {
        setStatus("error");
        setError("Withdraw is not available for this wallet.");
        return;
      }

      if (!rewardRouterAddress) {
        setStatus("error");
        setError("Withdraw contract address is not available.");
        return;
      }

      try {
        setStatus("submitting");
        setError("");

        const tx = buildWithdrawTransaction(position.poolId, amountRaw);
        if (!tx) {
          setStatus("error");
          setError("Withdraw contract address is not available.");
          return;
        }

        trackFunnelOnce("submitted");

        const hash = await sendTransaction({
          ...tx,
          gas:
            estimate?.gasLimit && estimate.gasLimit > 0n
              ? estimate.gasLimit
              : undefined,
          maxFeePerGas:
            estimate?.maxFeePerGas && estimate.maxFeePerGas > 0n
              ? estimate.maxFeePerGas
              : undefined,
          maxPriorityFeePerGas:
            estimate?.maxPriorityFeePerGas &&
            estimate.maxPriorityFeePerGas > 0n
              ? estimate.maxPriorityFeePerGas
              : undefined,
        });

        setTxHash(hash);
        setStatus("success");
        trackFunnelOnce("confirmed");
        invalidatePortfolioQueries();
      } catch (err) {
        setStatus("error");
        setError(toDisplayWalletError(err, "Failed to request withdraw."));
        trackFunnelOnce("confirmed", {
          status: "failure",
          reason: normalizeFailureReason(err),
        });
      }
    },
    [
      buildWithdrawTransaction,
      invalidatePortfolioQueries,
      rewardRouterAddress,
      sendTransaction,
      trackFunnelOnce,
    ],
  );

  return {
    canWithdrawInline,
    isOpen,
    initialPoolAddress,
    status,
    txHash,
    error,
    copied,
    hasEstimator: !!estimateGas,
    openWithdraw,
    closeWithdraw,
    estimateWithdraw,
    confirmWithdraw,
    copyHash,
    trackFunnelOnce,
  };
}
